import { useState, type FormEvent } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db8b6ark13ra8mig8p20';

type ParsedResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

const contactInfo = [
  {
    icon: 'ri-mail-line',
    label: '电子邮箱',
    value: 'contact@51daily-match.com',
    desc: '内容合作、数据纠错与商务咨询',
  },
  {
    icon: 'ri-phone-line',
    label: '联系电话',
    value: '400-800-5115',
    desc: '工作日 09:00 - 18:00',
  },
  {
    icon: 'ri-map-pin-line',
    label: '办公地址',
    value: '上海市浦东新区世纪大道 100 号 18 层',
    desc: '来访请提前预约',
  },
  {
    icon: 'ri-customer-service-2-line',
    label: '在线支持',
    value: '通过右侧表单提交反馈',
    desc: '我们会在 1-2 个工作日内回复',
  },
];

const feedbackTypes = ['内容纠错', '数据问题', '合作意向', '功能建议', '其他'];

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  usePageMeta({
    title: '联系我们 - 51每日大赛',
    description:
      '通过邮箱、电话或在线表单联系51每日大赛团队。欢迎提交内容纠错、数据问题与商务合作咨询，我们会在1-2个工作日内回复。',
    keywords: '联系我们,51每日大赛,联系方式,反馈表单,商务合作',
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    const formData = new FormData(form);
    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    setStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, typeof value === 'string' ? value : '');
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试或通过邮箱联系我们。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交未成功，请稍后重试或通过邮箱联系我们。');
    }
  };

  return (
    <div className="w-full px-4 md:px-6 pt-6 md:pt-8 pb-10 md:pb-12">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '联系我们' }]} />

      <div className="mt-4 bg-primary-900 rounded-lg relative overflow-hidden px-5 md:px-8 py-8 md:py-12">
        <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full bg-accent-500/15" />
        <div className="relative max-w-3xl">
          <span className="inline-block text-xs font-medium tracking-widest text-accent-400 uppercase mb-2">
            Contact
          </span>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">联系我们</h1>
          <p className="mt-3 text-sm md:text-base text-primary-200 leading-relaxed">
            无论你是想反馈内容问题、补充赛事数据，还是有合作意向，都可以通过以下方式与我们取得联系。
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
        <div className="lg:col-span-2 flex flex-col gap-4">
          {contactInfo.map((item) => (
            <div key={item.label} className="bg-background-50 border border-background-200 rounded-lg p-5 flex gap-4">
              <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                <i className={`${item.icon} w-5 h-5 flex items-center justify-center text-lg`} />
              </span>
              <div className="min-w-0">
                <p className="text-xs text-foreground-500">{item.label}</p>
                <p className="mt-1 text-sm font-medium text-foreground-950 break-words">{item.value}</p>
                <p className="mt-1 text-xs text-foreground-500">{item.desc}</p>
              </div>
            </div>
          ))}

          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="w-full h-56 bg-background-200">
              <iframe
                title="51每日大赛办公地址地图"
                aria-label="51每日大赛办公地址地图"
                src="https://jyelac.cn/maps?q=Shanghai%20Pudong%20Century%20Avenue&t=&z=13&ie=UTF8&iwloc=&output=embed"
                className="w-full h-full border-0"
              />
            </div>
            <div className="px-5 py-3 border-t border-background-200 flex items-center gap-2 text-xs text-foreground-600">
              <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center text-primary-600" />
              上海市浦东新区世纪大道 100 号
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-7">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 flex items-center gap-2">
              <i className="ri-edit-2-line text-accent-600 w-5 h-5 flex items-center justify-center" />
              在线反馈表单
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              请填写以下信息，带 <span className="text-accent-600">*</span> 为必填项。
            </p>

            <form data-readdy-form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                    姓名 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入您的称呼"
                    className="w-full bg-background-100 border border-background-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                    邮箱 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="w-full bg-background-100 border border-background-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-type" className="block text-sm text-foreground-700 mb-1.5">
                  反馈类型 <span className="text-accent-600">*</span>
                </label>
                <select
                  id="contact-type"
                  name="feedbackType"
                  required
                  defaultValue=""
                  className="w-full bg-background-100 border border-background-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:bg-background-50 transition-colors cursor-pointer"
                >
                  <option value="" disabled>
                    请选择反馈类型
                  </option>
                  {feedbackTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                  反馈内容 <span className="text-accent-600">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述您的问题或建议（最多 500 字）"
                  className="w-full bg-background-100 border border-background-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:bg-background-50 transition-colors resize-none"
                />
                <p className="mt-1 text-xs text-foreground-500">最多 500 字</p>
              </div>

              <input
                className="contact-form-alt-field"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" />
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center" />
                      提交反馈
                    </>
                  )}
                </button>
                {status === 'success' ? (
                  <span className="flex items-center gap-1.5 text-sm text-secondary-700">
                    <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center" />
                    提交成功，感谢您的反馈，我们会尽快处理。
                  </span>
                ) : null}
              </div>

              {status === 'error' ? (
                <div className="flex items-start gap-2 rounded-md bg-accent-50 border border-accent-200 px-4 py-3">
                  <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center text-accent-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-accent-800">{formError}</p>
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}